import * as mutationTypes from './mutation-types'

import SFetch from '@app/utils/sfetch'

import api from '../api'
import serialize from './mixins/serialize'
import { segmentVisibilities } from '../types'

const defaultVisibility = segmentVisibilities.PRIVATE_VISIBLE
const defaultSegment = {
  id: null,
  name: '',
  recommended: false,
  visibility: defaultVisibility,
  permissions: {},
  partner: '',
  insight_triggers_count: 0,
}

const getters = {
  isSegmentModified(state, getters, rootState) {
    if (state.isSegmentLoading) {
      return false
    }
    if (!state.segment || !state.segment.id) {
      return false
    }
    if (getters.isActiveFiltersModified) {
      return true
    }
    if (rootState.ledgerSelectDate !== (state.segment.period_date || null)) {
      return true
    }
    if (rootState.periodSelection !== state.segment.period_type) {
      return true
    }
  },
  lastSegmentRef(state, getters) {
    const currentSegment = state.segment || {}
    return !getters.isSegmentModified && currentSegment.last_request_ref
  },
}

const mutations = {
  [mutationTypes.SET_SEGMENTS](state, segments) {
    state.segments = segments
  },

  [mutationTypes.SET_SEGMENT](state, segment) {
    state.segment = segment
  },

  [mutationTypes.UPDATE_SEGMENT](state, newSegment) {
    const index = state.segments.findIndex(
      (currentSegment) => currentSegment.id === newSegment.id
    )
    state.segments[index] = newSegment
  },

  [mutationTypes.REMOVE_SEGMENT](state, segmentId) {
    state.segments = state.segments.filter(
      (segment) => segment.id !== segmentId
    )
  },

  [mutationTypes.SET_EDITED_SEGMENT](state, segment) {
    const newSegment = { ...(segment || {}) }
    // initialize visibility and permissions if not present
    if (!newSegment.visibility) {
      newSegment.visibility = defaultVisibility
    }

    if (!newSegment.permissions) {
      newSegment.permissions = {}
    }

    state.editedSegment = newSegment
  },

  [mutationTypes.UNSET_EDITED_SEGMENT](state) {
    state.editedSegment = defaultSegment
  },

  [mutationTypes.SET_SEGMENT_MODAL_VISIBLE](state, visible) {
    state.segmentModalVisible = visible
  },

  [mutationTypes.SET_SEGMENT_MODAL_TYPE](state, type) {
    state.segmentModalType = type
  },
  [mutationTypes.SEGMENT_START_LOADING](state) {
    state.isSegmentLoading = true
  },
  [mutationTypes.SEGMENT_STOP_LOADING](state) {
    state.isSegmentLoading = false
  },
}

const actions = {
  openSaveSegmentModal({ state, commit }) {
    commit(mutationTypes.SET_EDITED_SEGMENT, state.segment)
    commit(mutationTypes.SET_SEGMENT_MODAL_TYPE, 'save')
  },

  openSaveAsSegmentModal({ state, commit }) {
    const segment = {
      ...state.segment,
      visibility: defaultVisibility, // force new segments to be private by default
    }
    commit(mutationTypes.SET_EDITED_SEGMENT, segment)
    commit(mutationTypes.SET_SEGMENT_MODAL_TYPE, 'save_as')
  },

  openEditSegmentModal({ commit }, segment) {
    commit(mutationTypes.SET_EDITED_SEGMENT, segment)
    commit(mutationTypes.SET_SEGMENT_MODAL_TYPE, 'edit')
  },

  closeEditSegmentModal({ commit }) {
    commit(mutationTypes.UNSET_EDITED_SEGMENT)
    commit(mutationTypes.SET_SEGMENT_MODAL_TYPE, null)
    commit(mutationTypes.SET_SEGMENT_MODAL_VISIBLE, false)
  },

  unselectSegment({ commit, dispatch }) {
    commit(mutationTypes.SET_SEGMENT, null)
    commit(mutationTypes.SET_LEDGER_SELECT_DATE, null)
    commit(mutationTypes.SET_PERIOD_SELECTION, null)
    dispatch('unsetActiveFilters')
  },

  removeSegmentById({ commit }, segmentId) {
    commit(mutationTypes.REMOVE_SEGMENT, segmentId)
  },

  setEditedSegment({ commit }, segment) {
    commit(mutationTypes.SET_EDITED_SEGMENT, segment)
  },

  setDefaultPeriod({ commit }) {
    // select default period => "ending today" is added by default when setting the period type
    commit(mutationTypes.SET_PERIOD_SELECTION, 'financial_year')
  },

  showSegmentModal({ commit }) {
    commit(mutationTypes.SET_SEGMENT_MODAL_VISIBLE, true)
  },

  hideSegmentModal({ commit }) {
    commit(mutationTypes.SET_SEGMENT_MODAL_VISIBLE, false)
  },

  async fetchSegments({ commit }) {
    const segments = await api.fetchSegments()

    commit(mutationTypes.SET_SEGMENTS, segments)
  },

  addToRecent({ commit }, segment) {
    commit(mutationTypes.ADD_RECENT_SEGMENT, segment.id)
  },

  async loadSegment({ commit, dispatch }, segment) {
    commit(mutationTypes.SET_SEGMENT, segment)
    commit(mutationTypes.SEGMENT_START_LOADING)
    // clear companies once we start loading a segment:
    // this is done slightly later anyway, but this way we're more consistent with the loading messages
    commit(mutationTypes.INIT_COMPANIES_REQUEST)

    const firmId = Silverfin.currentFirmId

    // eslint-disable-next-line camelcase
    const response = await api.fetchSegment({
      segmentId: segment.id,
      partnerSegmentId: segment.partner_segment_id,
    })

    if (Object.keys(response).length > 0) {
      const { segment_filters, ...segment } = response

      commit(mutationTypes.SET_SEGMENT, segment)
      commit(mutationTypes.SEGMENT_STOP_LOADING)
      dispatch('fetchTriggers', segment.id)
      dispatch('setActiveFilters', segment_filters)
      dispatch('setLedgerSelectDate', segment.period_date)
      dispatch('setPeriodSelection', segment.period_type)
    } else {
      // reload Insights page if segment not found
      window.location.href =
        Silverfin.Routes.companies_overview_index_path(firmId)
    }
  },

  async silentReloadCurrentSegment({ commit, state }) {
    const currentSegment = state.segment

    if (currentSegment) {
      const response = await api.fetchSegment({
        segmentId: currentSegment.id,
        partnerSegmentId: currentSegment.partner_segment_id,
      })

      // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
      const { segment_filters, ...segment } = response

      commit(mutationTypes.UPDATE_SEGMENT, segment)
      commit(mutationTypes.SET_SEGMENT, segment)
    }
  },

  async createSegment({ commit, dispatch, state, rootState, getters }) {
    const { id, ...segment } = state.editedSegment
    const firmId = Silverfin.currentFirmId
    const segmentsEndpoint = Silverfin.Routes.segments_path(firmId)

    const body = {
      segment: {
        ...segment,
        filters: serialize.filterSettings(
          getters.getActiveFilters,
          rootState.filters.allFiltersByType,
          { includeId: !id }
        ),
        period_date: rootState.ledgerSelectDate,
        period_type: rootState.periodSelection,
      },
    }

    const { segment_filters, ...responseSegment } = await SFetch.json(
      segmentsEndpoint,
      {
        method: 'POST',
        body,
      },
      { camelizeJSON: false }
    )

    dispatch('closeEditSegmentModal')
    commit(mutationTypes.SET_SEGMENT, responseSegment)
    commit(mutationTypes.ADD_RECENT_SEGMENT, responseSegment.id)
    dispatch('setActiveFilters', segment_filters)
    dispatch('fetchSegments')
    dispatch('fetchTriggers', responseSegment.id)
  },

  async saveAsNewSegment({ dispatch, state }, { name, visibility }) {
    // in case the Segment modal is open for a non-current segment,
    // we need to load the full segment (filters etc)
    if (state.editedSegment.id !== state.segment?.id) {
      await dispatch('loadSegment', state.editedSegment)
    }

    const segment = {
      ...state.segment,
      visibility: visibility,
      name: name,
    }

    // set it as the edited segment, then create it as new
    dispatch('setEditedSegment', segment)
    await dispatch('createSegment')
  },

  async updateSegmentName({ commit, dispatch, state }, name) {
    const { id } = state.segment
    const firmId = Silverfin.currentFirmId
    const segmentEndpoint = Silverfin.Routes.segment_path(firmId, id)

    try {
      const body = { segment: { name } }
      const { segment_filters, ...segment } = await SFetch.json(
        segmentEndpoint,
        { method: 'PATCH', body },
        { camelizeJSON: false }
      )

      commit(mutationTypes.UPDATE_SEGMENT, { ...segment, name })
      commit(mutationTypes.SET_SEGMENT, segment)
      dispatch('setActiveFilters', segment_filters)
      dispatch('setLedgerSelectDate', segment.period_date)
    } catch (e) {
      alert(e.body.errorMessage)
    }
  },

  updatePartnerSegment({ rootState }, partnerSegmentID) {
    const editUrl =
      Silverfin.Routes.edit_partners_template_collection_segment_path({
        id: partnerSegmentID,
      })
    const segmentParams = JSON.stringify({
      filters: serialize.filterSettings(
        rootState.filters.activeFilters,
        rootState.filters.allFiltersByType
      ),
      period_date: rootState.ledgerSelectDate,
      period_type: rootState.periodSelection,
    })
    window.location.href = `${editUrl}?firm_segment=${encodeURIComponent(
      segmentParams
    )}`
  },

  async updateSegment(
    { commit, dispatch, state, rootState, getters },
    updatedSegment = null
  ) {
    const { id, ...segment } = updatedSegment || state.segment
    const firmId = Silverfin.currentFirmId
    const segmentEndpoint = Silverfin.Routes.segment_path(firmId, id)

    const payload = {
      ...segment,
      period_date: rootState.ledgerSelectDate,
      period_type: rootState.periodSelection,
    }

    // filters are handled brute-force-style in the backend, i.e. always recreated.
    // additionally, the cache is invalidated when filters "change". Thus, only send filters if indeed they've changed in the UI
    if (getters.isActiveFiltersModified) {
      payload.filters = serialize.filterSettings(
        getters.getActiveFilters,
        rootState.filters.allFiltersByType
      )
    }

    const { segment_filters, ...responseSegment } = await SFetch.json(
      segmentEndpoint,
      {
        method: 'PUT',
        body: { segment: payload },
      },
      { camelizeJSON: false }
    )

    // we might be updating a different segment than the current one
    if (id === state.segment?.id) {
      commit(mutationTypes.SET_SEGMENT, responseSegment)
      dispatch('setActiveFilters', segment_filters)
      dispatch('setLedgerSelectDate', responseSegment.period_date)
    }

    commit(mutationTypes.UPDATE_SEGMENT, responseSegment)
    dispatch('closeEditSegmentModal')
  },

  async deleteSegment({ commit, state }, { id } = {}) {
    const segmentId = id || state.segment.id
    const firmId = Silverfin.currentFirmId
    const segmentEndpoint = Silverfin.Routes.segment_path(firmId, segmentId)

    try {
      await SFetch.json(
        segmentEndpoint,
        {
          method: 'DELETE',
        },
        { camelizeJSON: false }
      )
      commit(mutationTypes.SET_SEGMENT, null)
      commit(mutationTypes.REMOVE_SEGMENT, segmentId)
    } catch (e) {
      alert(e.body.errorMessage)
    }
  },
}

export default {
  state() {
    return {
      segmentModalVisible: false,
      segments: [],
      segment: null,
      isSegmentLoading: false,
      editedSegment: { ...defaultSegment },
      segmentModalType: null,
      recentSegments: [],
    }
  },
  getters,
  mutations,
  actions,
}
